import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { GetTreatmentResultResponse } from 'it/primatoo/openapi/api/org/schemas/treatment/GetTreatmentResultResponse';
import { Chapter } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listtemplatechapterresponse/Chapter';
import { DetailsChartsDto, TREATMENT_GAUGE_PALETTE_MAX_9, TreatmentChart } from '@pt/pt-models';
import { RiskEvaluationGroup } from 'it/primatoo/openapi/api/org/schemas/treatment/riskassessment/RiskEvaluationGroup';
import { TreatmentService } from '@pt/api-client';
import { ActionsSubject, Store } from '@ngrx/store';
import { NgrxWidgetActions } from '../store';
import { ChapterSelector } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterSelector';
import { forceReloadedResult } from '../store/actions/widget.actions';
import { ofType } from '@ngrx/effects';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DpiaEvalRow } from 'it/primatoo/openapi/api/org/schemas/dpia/dpiaevaluation/DpiaEvalRow';
import { DpiaColumn } from 'it/primatoo/openapi/api/org/schemas/dpia/dpiaevaluation/DpiaColumn';

@UntilDestroy()
@Component({
  selector: 'pt-treatment-result',
  template: `
    <div class="mb-2 mt-4">
      <ng-container [ngSwitch]='currentSelector'>
        <div *ngSwitchCase="CS.TREAT_TREAT">
          <pt-details-chart
            [showReloadButton]='!autoReload && requireReload'
            [title]='"LABELS.RISK_FACTOR" | translate'
            [gaugeColor]='treatmentGaugeColor'
            [charts]='treatmentRiskFactors'
            (reload)='reloadResult()'>
          </pt-details-chart>
        </div>
        <div *ngSwitchCase="CS.TREAT_DPIA">
          <pt-dpia-evaluation-map
            [containerCssClass]='"mt-4"'>
          </pt-dpia-evaluation-map>

          <pt-dpia-comments
            [containerCssClass]='"mt-2"'
            [docId]="docId">
          </pt-dpia-comments>

            <pt-dpia-stats-editor
              [rows]="rows"
              [columns]="columns"
              [showReloadButton]='!autoReload && requireReload'
              [mode]='"DIALOG"'
              (reload)='reloadResult()'>
            </pt-dpia-stats-editor>
        </div>

        <div class="position-relative" *ngSwitchCase="CS.TREAT_RA">

          <pt-required-reload
            class="display-contents"
            [requiredReload]="!autoReload && requireReload"
            (reload)="reloadResult()">
          </pt-required-reload>

          <pt-risk-assessment-stats
            [ngClass]="{'opacity-25': !autoReload && requireReload}"
            [mode]='"tab"'
            [showReload]="false"
            [evaluation]='riskAssessmentEvaluation'
            (reloadStats)="reloadResult()"
          ></pt-risk-assessment-stats>
        </div>

      </ng-container>
    </div>

  `
})
export class TreatmentResultComponent implements OnChanges, OnInit {
  @Input() docId: number;
  @Input() chapter: Chapter;
  @Input() requireReload: boolean;
  @Input() autoReload = false;
  @Input() appScore = 0;
  @Input() treatmentStats: TreatmentChart[];
  public treatmentGaugeColor = TREATMENT_GAUGE_PALETTE_MAX_9;
  public treatmentRiskFactors: DetailsChartsDto[];
  public treatmentAppScore: DetailsChartsDto[];
  public riskAssessmentEvaluation: Array<RiskEvaluationGroup>;
  // il selettore check mantiene i result del precedente capitolo aperto
  public currentSelector: ChapterSelector;
  CS = ChapterSelector
  // DPIA
  public columns: DpiaColumn[] = [];
  public rows: DpiaEvalRow[] = [];
  private result: GetTreatmentResultResponse;

  constructor(private service: TreatmentService,
              private actionsSubject: ActionsSubject,
              private store: Store) {
  }

  ngOnInit(): void {
    forceReloadedResult;
    this.actionsSubject.pipe(
      ofType(
        NgrxWidgetActions.forceReloadedResult
      ),
      untilDestroyed(this)
    ).subscribe((action) => {
      this.reloadResult();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.docId) {
      this.reloadResult();
    }

    if (changes.requireReload) {
      if (this.requireReload && this.autoReload) {
        this.reloadResult();
      }
    }

    if (changes.chapter) {
      this.alignWidget(this.result, changes.chapter.currentValue);
    }


  }

  public reloadResult() {
    if (this.docId) {
      this.service.getResult(this.docId).subscribe((result: GetTreatmentResultResponse) => {
        this.result = result;
        this.alignWidget(this.result, this.chapter);
        this.store.dispatch(NgrxWidgetActions.reloadedResult());
      }, () => {
        this.result = undefined;
        this.store.dispatch(NgrxWidgetActions.reloadedResult());
      });
    } else {
      this.result = undefined;
      this.store.dispatch(NgrxWidgetActions.reloadedResult());
    }
  }

  private alignWidget(result: GetTreatmentResultResponse, chapter: Chapter) {
    if (result) {
      if (chapter && chapter?.selector && chapter?.selector !== ChapterSelector.TREAT_CHECK) {
        this.currentSelector = chapter.selector;
      }

      this.treatmentAppScore = [{
        label: null,
        textValue: null,
        value: this.appScore,
        max: this.appScore
      } as DetailsChartsDto
      ]
      this.treatmentRiskFactors = [{
        label: null,
        textValue: null,
        value: result?.treatmentRiskFactors,
        max: 5
      } as DetailsChartsDto];

      this.riskAssessmentEvaluation = result?.riskAssessment?.groups;
      if (this.result?.dpia) {
        this.rows = result.dpia.rows || []
        this.columns = result.dpia.columns || [];
      } else {
        this.rows = []
        this.columns = [];
      }
    }
  }
}
